import * as React from "react";

import { Typography } from "@material-ui/core";

import { MerchantAgreementLink } from "client/domain/Link/MerchantAgreementLink";
import { PrivacyPolicyLink } from "client/domain/Link/PrivacyPolicyLink";
import { useBranding, useMerchantModel } from "client/hooks";
import { CountryList, Product } from "client/lib/apt-merchant/src/types";

const getAfterpayPrivacyPolicyText = (
  requiresSpecialTerms = false
): Record<CountryList, React.ReactElement> => ({
  "United States": (
    <Typography variant="caption">
      I, a duly authorized representative or sole proprietor of the above
      Company, have read and agree to the{" "}
      <MerchantAgreementLink linkOrigin="Agreement" product="Afterpay" />.
      Afterpay collects and handles personal data in accordance with Afterpay
      &apos;s <PrivacyPolicyLink />. If I provide Afterpay personal data on
      behalf of another individual, I confirm they have agreed that I can
      disclose it to Afterpay for these purposes. Under penalties of perjury, I
      certify that: 1. The number shown on this form is my correct taxpayer
      identification number; and 2. I am not subject to backup withholding
      because: (a) I am exempt from backup withholding, or (b) I have not been
      notified by the Internal Revenue Service (IRS) that I am subject to backup
      withholding as a result of a failure to report all interest or dividends,
      or (c) the IRS has notified me that I am no longer subject to backup
      withholding; and 3. I am a U.S. citizen or other U.S. person; and 4. The
      FATCA code(s) entered on this form (if any) indicating that I am exempt
      from FATCA reporting is correct.
    </Typography>
  ),
  Australia: (
    <Typography variant="caption">
      I, a duly authorised representative of the merchant, have read and agree
      to the terms of the{" "}
      <MerchantAgreementLink linkOrigin="Agreement" product="Afterpay" />{" "}
      {requiresSpecialTerms ? " (and special terms)" : ""}, and warrant that I
      have full legal authority to accept the Agreement on behalf of the
      merchant. We collect and handle personal data in accordance with our{" "}
      <PrivacyPolicyLink />. We will disclose personal data to credit reporting
      bodies and other external agencies to verify your identity and assess your
      suitability. If you provide Afterpay personal data on behalf of another
      individual, you confirm they have agreed that you can disclose it to us
      for these purposes.
    </Typography>
  ),
  "United Kingdom": (
    <Typography variant="caption">
      I, a duly authorized representative or sole trader, have read and agree to
      the <MerchantAgreementLink linkOrigin="Agreement" product="Afterpay" />.{" "}
      We collect and handle personal data in accordance with our{" "}
      <PrivacyPolicyLink />. We will disclose personal data to credit reporting
      bodies and other external agencies to verify your identity and assess your
      suitability. If you provide Clearpay personal data on behalf of another
      individual, you confirm they have agreed that you can disclose it to us
      for these purposes.
    </Typography>
  ),
  Canada: (
    <Typography variant="caption">
      I, a duly authorized representative or sole trader, have read and agree to
      the <MerchantAgreementLink linkOrigin="Agreement" product="Afterpay" />. I
      consent to Afterpay disclosing my personal data to credit reporting bodies
      and other external agencies to verify my identity and assess my
      suitability. We collect and handle personal data in accordance with our{" "}
      <PrivacyPolicyLink />. If I am providing Afterpay personal data on behalf
      of another individual, I confirm they have consented.
    </Typography>
  ),
  "New Zealand": (
    <Typography variant="caption">
      I, a duly authorised representative of the merchant, have read and agree
      to the terms of the{" "}
      <MerchantAgreementLink linkOrigin="Agreement" product="Afterpay" />, and
      warrant that I have full legal authority to accept the Agreement on behalf
      of the merchant. I consent to Afterpay disclosing my personal data to
      credit reporting bodies and other external agencies to verify my identity
      and assess my suitability as required by law. We collect and handle
      personal data in accordance with our <PrivacyPolicyLink />. If I am
      providing Afterpay personal data on behalf of another individual, I
      confirm they have consented.
    </Typography>
  ),
});

const CashPrivacyPolicyText: React.FC = () => {
  const { brandTitle } = useBranding();

  return (
    <Typography variant="caption">
      I, a duly authorized representative or sole proprietor of the above
      Company, have read and agree to the{" "}
      <MerchantAgreementLink linkOrigin="Agreement" product="Cash" />.{" "}
      {brandTitle} collects and handles personal data in accordance with{" "}
      <PrivacyPolicyLink product="Cash" />. If I provide {brandTitle} personal
      data on behalf of another individual, I confirm they have agreed that I
      can disclose it to {brandTitle} for these purposes.
    </Typography>
  );
};

interface PrivacyPolicyProps {
  product: Product;
}

const supportedProducts: Product[] = ["Afterpay", "Cash"];

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ product }) => {
  const { merchant } = useMerchantModel();
  const country = merchant.companyInformation.registeredCountry.value;
  const industry = merchant.storeDetails.industry.value;
  const requiresSpecialTerms = industry === "Adult" || industry === "Health";
  if (!country || !supportedProducts.includes(product)) {
    return null;
  }

  // Currently returning the same content for CashPay agreements regardless of country
  return product === "Afterpay" ? (
    getAfterpayPrivacyPolicyText(requiresSpecialTerms)[country]
  ) : (
    <CashPrivacyPolicyText />
  );
};
